import {Platform, useWindowDimensions} from 'react-native';

import {
  MODAL_MAX_HEIGHT_PERCENTAGE,
  MODAL_MIN_WIDTH,
  MODAL_SIZE_MAP,
  MODAL_WIDTH_PERCENTAGE,
  ModalSize,
} from '../constants/modalDimensions';

import {useVisualViewportHeight} from './useVisualViewportHeight';

type ModalDimensionsOptions = {
  /** Modal size tier: small (360px), medium (480px), large (600px). Default: medium */
  size?: ModalSize;
  /** Override maxWidth for edge cases */
  customMaxWidth?: number;
  /** Enable maxHeight constraint (90% screen). Use for scrollable/form modals */
  constrainHeight?: boolean;
};

type ModalDimensions = {
  /** Calculated width respecting screen size and maxWidth */
  width: number;
  /** Minimum width, capped at calculated width to prevent overflow on small screens */
  minWidth: number;
  /** Maximum height if constrainHeight is true, undefined otherwise */
  maxHeight: number | undefined;
  /** Pre-built style object for spreading into View style */
  containerStyle: {
    width: number;
    minWidth: number;
    maxHeight?: number;
  };
};

/**
 * Calculate responsive modal dimensions based on screen size and modal type.
 *
 * @example
 * // Form modal (medium, with height constraint)
 * const {containerStyle} = useModalDimensions({size: 'medium', constrainHeight: true});
 *
 * @example
 * // Alert modal (small, no height constraint)
 * const {containerStyle} = useModalDimensions({size: 'small'});
 */
export function useModalDimensions(options: ModalDimensionsOptions = {}): ModalDimensions {
  const {size = 'medium', customMaxWidth, constrainHeight = false} = options;
  const {width: screenWidth, height: screenHeight} = useWindowDimensions();
  const {height: visualViewportHeight} = useVisualViewportHeight();

  // On mobile web, use visual viewport height which shrinks when the keyboard opens.
  // This ensures height-constrained modals stay within the visible area above the keyboard.
  const effectiveHeight =
    Platform?.OS === 'web' && visualViewportHeight > 0 ? visualViewportHeight : screenHeight;

  const maxWidth = customMaxWidth ?? MODAL_SIZE_MAP[size];
  const width = Math.min(screenWidth * MODAL_WIDTH_PERCENTAGE, maxWidth);
  // Ensure minWidth never exceeds calculated width to prevent overflow on small screens
  const minWidth = Math.min(MODAL_MIN_WIDTH, width);
  const maxHeight = constrainHeight ? effectiveHeight * MODAL_MAX_HEIGHT_PERCENTAGE : undefined;

  return {
    width,
    minWidth,
    maxHeight,
    containerStyle: {
      width,
      minWidth,
      ...(maxHeight !== undefined && {maxHeight}),
    },
  };
}
